import {
    AccountType,
    DefaultCashBasisAccountCodes,
} from "@common/constants/domains/accounting/accounts";
import {
    isScheduleHoldingAccountType,
    isScheduleRecognitionAccountType,
} from "@common/constants/domains/accounting/scheduleAccountFilters";
import {
    ExpenseSubMode,
    RevenueSubMode,
    ScheduleType,
} from "@common/types/domains/accounting/schedule";

export interface KickDefaultScheduleAccountCandidate {
    id: string;
    type: AccountType;
    kickOriginalCode: number | null;
    isDisabled: boolean;
}

export interface KickDefaultScheduleAccountIds {
    holdingAccountId: string;
    recognitionAccountId: string;
}

function preferredHoldingCode(revenueSubMode: RevenueSubMode): number {
    if (revenueSubMode === RevenueSubMode.deferred) {
        return DefaultCashBasisAccountCodes.deferredRevenue;
    }
    return DefaultCashBasisAccountCodes.accruedRevenue;
}

function isUsableAccount(
    account: KickDefaultScheduleAccountCandidate,
): boolean {
    return !account.isDisabled;
}

function findPreferredAccount(options: {
    accounts: KickDefaultScheduleAccountCandidate[];
    kickOriginalCode: number;
    isAllowed: (account: KickDefaultScheduleAccountCandidate) => boolean;
}): KickDefaultScheduleAccountCandidate | null {
    return (
        options.accounts.find(
            (account) =>
                isUsableAccount(account) &&
                account.kickOriginalCode === options.kickOriginalCode &&
                options.isAllowed(account),
        ) ?? null
    );
}

function findUniqueAllowedAccount(options: {
    accounts: KickDefaultScheduleAccountCandidate[];
    isAllowed: (account: KickDefaultScheduleAccountCandidate) => boolean;
}): KickDefaultScheduleAccountCandidate | null {
    const matches = options.accounts.filter(
        (account) => isUsableAccount(account) && options.isAllowed(account),
    );
    if (matches.length !== 1) {
        return null;
    }
    return matches[0];
}

function preferredExpenseHoldingCode(expenseSubMode: ExpenseSubMode): number {
    if (expenseSubMode === ExpenseSubMode.prepaid) {
        return DefaultCashBasisAccountCodes.prepaidExpenses;
    }
    return DefaultCashBasisAccountCodes.accruedExpenses;
}

type RecognitionFallback = "uniqueAllowed" | "none";

function resolveRecognitionFallback(options: {
    accounts: KickDefaultScheduleAccountCandidate[];
    recognitionFallback: RecognitionFallback;
    isRecognitionAccount: (
        account: KickDefaultScheduleAccountCandidate,
    ) => boolean;
}): KickDefaultScheduleAccountCandidate | null {
    if (options.recognitionFallback === "none") {
        return null;
    }
    return findUniqueAllowedAccount({
        accounts: options.accounts,
        isAllowed: options.isRecognitionAccount,
    });
}

function resolveAccountPair(options: {
    accounts: KickDefaultScheduleAccountCandidate[];
    holdingCode: number;
    recognitionCode: number;
    recognitionFallback: RecognitionFallback;
    isHoldingAccount: (account: KickDefaultScheduleAccountCandidate) => boolean;
    isRecognitionAccount: (
        account: KickDefaultScheduleAccountCandidate,
    ) => boolean;
}): KickDefaultScheduleAccountIds | null {
    const holdingAccount =
        findPreferredAccount({
            accounts: options.accounts,
            kickOriginalCode: options.holdingCode,
            isAllowed: options.isHoldingAccount,
        }) ??
        findUniqueAllowedAccount({
            accounts: options.accounts,
            isAllowed: options.isHoldingAccount,
        });
    const recognitionAccount =
        findPreferredAccount({
            accounts: options.accounts,
            kickOriginalCode: options.recognitionCode,
            isAllowed: options.isRecognitionAccount,
        }) ?? resolveRecognitionFallback(options);
    if (holdingAccount == null || recognitionAccount == null) {
        return null;
    }
    if (holdingAccount.id === recognitionAccount.id) {
        return null;
    }
    return {
        holdingAccountId: holdingAccount.id,
        recognitionAccountId: recognitionAccount.id,
    };
}

export function resolveKickDefaultScheduleAccountIds(options: {
    revenueSubMode: RevenueSubMode;
    accounts: readonly KickDefaultScheduleAccountCandidate[];
}): KickDefaultScheduleAccountIds | null {
    const accounts = [...options.accounts];
    return resolveAccountPair({
        accounts,
        holdingCode: preferredHoldingCode(options.revenueSubMode),
        recognitionCode: DefaultCashBasisAccountCodes.revenue,
        recognitionFallback: "uniqueAllowed",
        isHoldingAccount: (account) =>
            isScheduleHoldingAccountType({
                accountType: account.type,
                mode: {
                    type: ScheduleType.revenue,
                    revenueSubMode: options.revenueSubMode,
                    expenseSubMode: null,
                },
            }),
        isRecognitionAccount: (account) =>
            isScheduleRecognitionAccountType({
                accountType: account.type,
                scheduleType: ScheduleType.revenue,
            }),
    });
}

export function resolveKickDefaultExpenseScheduleAccountIds(options: {
    expenseSubMode: ExpenseSubMode;
    recognitionCode: number;
    accounts: readonly KickDefaultScheduleAccountCandidate[];
}): KickDefaultScheduleAccountIds | null {
    const accounts = [...options.accounts];
    return resolveAccountPair({
        accounts,
        holdingCode: preferredExpenseHoldingCode(options.expenseSubMode),
        recognitionCode: options.recognitionCode,
        recognitionFallback: "none",
        isHoldingAccount: (account) =>
            isScheduleHoldingAccountType({
                accountType: account.type,
                mode: {
                    type: ScheduleType.expense,
                    revenueSubMode: null,
                    expenseSubMode: options.expenseSubMode,
                },
            }),
        isRecognitionAccount: (account) =>
            isScheduleRecognitionAccountType({
                accountType: account.type,
                scheduleType: ScheduleType.expense,
            }),
    });
}
